// Expense management TypeScript interfaces matching backend POJOs

export enum ExpenseStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL', 
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  INVOICED = 'INVOICED',
  PAID = 'PAID'
}

export enum ExpenseCategory {
  TRAVEL = 'TRAVEL',
  MEALS = 'MEALS',
  SUPPLIES = 'SUPPLIES',
  EQUIPMENT = 'EQUIPMENT',
  SOFTWARE = 'SOFTWARE',
  TRAINING = 'TRAINING',
  OTHER = 'OTHER'
}

export interface Expense {
  id: string;
  contractorEntityId: number;
  contractorContractId: string;
  amount: string; // Decimal as string from backend
  description: string;
  expenseDate: string; // ISO date string
  category: ExpenseCategory;
  receiptUrl?: string;
  receiptFilename?: string;
  status: ExpenseStatus;
  submittedAt?: string; // ISO datetime string
  submittedByEmail?: string;
  reviewedAt?: string; // ISO datetime string
  reviewedByEmail?: string;
  rejectionReason?: string;
  addedToInvoiceAt?: string; // ISO datetime string
  invoiceLineItemId?: number;
  notes?: string;
  createdAt: string; // ISO datetime string
}

export interface ExpenseFormData {
  contractorContractId: string;
  amount: string;
  description: string;
  expenseDate: string; // YYYY-MM-DD format
  category: ExpenseCategory;
  receiptUrl?: string;
  receiptFilename?: string;
  notes?: string;
}

export interface ExpenseApprovalData {
  approved: boolean;
  rejectionReason?: string;
  notes?: string;
}

export interface ExpenseListResponse {
  expenses: Expense[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface ExpenseStatusBreakdown {
  status: ExpenseStatus;
  count: number;
  totalAmount: string;
}

export interface ExpenseSummary {
  totalCount: number;
  totalApprovedAmount: string; // Sum of INVOICED expenses - Decimal as string
  pendingCount: number; // DRAFT expenses (UX: "Pending")
  pendingAmount: string;
  approvedCount: number; // INVOICED expenses (UX: "Approved") 
  approvedAmount: string;
  paidCount: number; // PAID expenses
  paidAmount: string;
  rejectedCount: number;
  rejectedAmount: string;
  // Legacy fields for backwards compatibility
  draftCount: number;
  draftAmount: string;
  invoicedCount: number;
  invoicedAmount: string;
  statusBreakdown: ExpenseStatusBreakdown[];
  recentExpenses: Expense[];
}

export interface ExpenseFilters {
  status?: ExpenseStatus[];
  category?: ExpenseCategory[];
  contractId?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  minAmount?: number;
  maxAmount?: number;
  search?: string;
}

export interface CreateExpenseRequest {
  contractorContractId: string;
  amount: string;
  description: string;
  expenseDate: string; // YYYY-MM-DD format
  category: ExpenseCategory;
  receiptUrl?: string;
  receiptFilename?: string;
}

export interface SubmitExpenseRequest {
  notes?: string;
}

export interface ExpenseContract {
  id: string;
  title: string;
  clientName: string;
  status: string;
}

// Category metadata for UI display
export interface ExpenseCategoryInfo {
  value: ExpenseCategory;
  label: string;
  description: string;
  icon: string;
  examples: string[];
}

export const EXPENSE_CATEGORY_INFO: Record<ExpenseCategory, ExpenseCategoryInfo> = {
  [ExpenseCategory.TRAVEL]: {
    value: ExpenseCategory.TRAVEL,
    label: 'Travel',
    description: 'Transportation and travel-related expenses',
    icon: '🚗',
    examples: ['Train tickets', 'Taxi fares', 'Parking', 'Mileage']
  },
  [ExpenseCategory.MEALS]: {
    value: ExpenseCategory.MEALS,
    label: 'Meals & Entertainment', 
    description: 'Business meals and client entertainment',
    icon: '🍽️',
    examples: ['Client lunches', 'Conference meals', 'Business dinners']
  },
  [ExpenseCategory.SUPPLIES]: {
    value: ExpenseCategory.SUPPLIES,
    label: 'Supplies',
    description: 'Office and business supplies',
    icon: '📦',
    examples: ['Stationery', 'Office materials', 'Printing costs']
  },
  [ExpenseCategory.EQUIPMENT]: {
    value: ExpenseCategory.EQUIPMENT,
    label: 'Equipment',
    description: 'Hardware and equipment purchases',
    icon: '💻',
    examples: ['Laptop', 'Monitor', 'Desk setup', 'Tools']
  },
  [ExpenseCategory.SOFTWARE]: {
    value: ExpenseCategory.SOFTWARE,
    label: 'Software',
    description: 'Software licenses and subscriptions',
    icon: '💿',
    examples: ['Software licenses', 'SaaS subscriptions', 'Apps']
  },
  [ExpenseCategory.TRAINING]: {
    value: ExpenseCategory.TRAINING,
    label: 'Training',
    description: 'Professional development and training',
    icon: '📚',
    examples: ['Courses', 'Certifications', 'Conference fees']
  },
  [ExpenseCategory.OTHER]: {
    value: ExpenseCategory.OTHER,
    label: 'Other',
    description: 'Other business-related expenses',
    icon: '📋',
    examples: ['Miscellaneous business expenses']
  }
};

// Status metadata for UI display
export interface ExpenseStatusInfo {
  value: ExpenseStatus;
  label: string;
  description: string;
  color: 'gray' | 'yellow' | 'green' | 'red' | 'blue';
  icon: string;
}

export const EXPENSE_STATUS_INFO: Record<ExpenseStatus, ExpenseStatusInfo> = {
  [ExpenseStatus.DRAFT]: {
    value: ExpenseStatus.DRAFT,
    label: 'Draft',
    description: 'Expense is being prepared',
    color: 'gray',
    icon: '✏️'
  },
  [ExpenseStatus.PENDING_APPROVAL]: {
    value: ExpenseStatus.PENDING_APPROVAL,
    label: 'Pending Approval',
    description: 'Waiting for client approval',
    color: 'yellow', 
    icon: '⏳'
  },
  [ExpenseStatus.APPROVED]: {
    value: ExpenseStatus.APPROVED,
    label: 'Approved',
    description: 'Approved and ready for invoicing',
    color: 'green',
    icon: '✅'
  },
  [ExpenseStatus.REJECTED]: {
    value: ExpenseStatus.REJECTED,
    label: 'Rejected',
    description: 'Rejected by client',
    color: 'red',
    icon: '❌'
  },
  [ExpenseStatus.INVOICED]: {
    value: ExpenseStatus.INVOICED,
    label: 'Invoiced',
    description: 'Added to invoice',
    color: 'blue',
    icon: '📄'
  },
  [ExpenseStatus.PAID]: {
    value: ExpenseStatus.PAID,
    label: 'Paid',
    description: 'Invoice has been paid',
    color: 'green',
    icon: '💰'
  }
}; 